// ============================================
// MessageInput Component
// Input field with attachments and emoji support
// ============================================

import React, { useState, useRef, useEffect } from 'react';
import { uploadAttachment } from '@/lib/supabase/chat';
import { compressImage } from '@/lib/image/compressImage';
import {
  Send,
  Paperclip,
  X,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SendMessageParams } from '@/types/chat';
import { useToast } from '@/hooks/use-toast';
import { motion, AnimatePresence } from 'framer-motion';

interface MessageInputProps {
  conversationId?: string;
  groupId?: string;
  recipientId?: string;
  onSend: (params: SendMessageParams) => Promise<void>;
  onTyping: (isTyping: boolean) => void;
  autoFocus?: boolean;
  disableAutoFocusOnMobile?: boolean;
}

export const MessageInput: React.FC<MessageInputProps> = ({
  conversationId,
  groupId,
  recipientId,
  onSend,
  onTyping,
  autoFocus = false,
  disableAutoFocusOnMobile = false,
}) => {
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [attachment, setAttachment] = useState<{
    url: string;
    type: string;
    name: string;
    size: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editableRef = useRef<HTMLDivElement>(null);
  const nativeTextareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  const isTouchInputDevice = () => {
    if (typeof window === 'undefined') return false;
    const hasTouchPoints = navigator.maxTouchPoints > 0;
    const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
    return Boolean(hasTouchPoints || coarsePointer);
  };

  useEffect(() => {
    setIsTouchDevice(isTouchInputDevice());
  }, []);

  // Auto-focus textarea when requested
  useEffect(() => {
    const isMobile = isTouchDevice;
    const inputEl = isMobile ? nativeTextareaRef.current : editableRef.current;
    
    if (autoFocus && inputEl && (!disableAutoFocusOnMobile || !isMobile)) {
      setTimeout(() => {
        inputEl.focus();
      }, 100);
    }
  }, [autoFocus, disableAutoFocusOnMobile, isTouchDevice]);

  // Handle typing indicator
  const handleTyping = () => {
    onTyping(true);
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => onTyping(false), 3000);
  };

  // Handle message change
  const handleEditableChange = () => {
    const text = editableRef.current?.innerText || '';
    setMessage(text);
    handleTyping();
  };

  const handleNativeTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    handleTyping();

    // Keep mobile textarea growth natural but bounded.
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'Archivo muy grande', description: 'Máximo 10MB.', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      let finalFile = file;
      if (file.type.startsWith('image/')) {
        finalFile = await compressImage(file, 1, 1200);
      }
      const result = await uploadAttachment(finalFile, conversationId, groupId);
      setAttachment(result);
    } catch (e: any) {
      toast({ title: 'Error al subir', description: e.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Handle message send
  const handleSend = async () => {
    // Si no hay contenido ni adjunto, o se está subiendo, no enviar
    if ((!message.trim() && !attachment) || uploading) return;

    const params: SendMessageParams = {
      recipient_id: recipientId,
      group_id: groupId,
      type: attachment ? 'attachment' : 'text',
      content: message.trim(),
    };

    if (attachment) {
      params.attachment_url = attachment.url;
      params.attachment_type = attachment.type;
      params.attachment_name = attachment.name;
      params.attachment_size = attachment.size;
    }

    // Reset input immediately
    setMessage('');
    setAttachment(null);
    if (editableRef.current) editableRef.current.innerText = '';
    if (nativeTextareaRef.current) {
      nativeTextareaRef.current.value = '';
      nativeTextareaRef.current.style.height = '40px';
    }
    
    try {
      await onSend(params);
      onTyping(false);
      // Mantener el focus para seguir escribiendo
      (isTouchDevice ? nativeTextareaRef.current : editableRef.current)?.focus();
    } catch (e: any) {
      toast({ title: 'Error al enviar', description: e.message, variant: 'destructive' });
    }
  };

  // Handle key down
  // Desktop: Enter to send, Shift+Enter for line break
  // Mobile: Enter for line break, Cmd/Ctrl+Enter to send
  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const isMobile = isTouchDevice;

    if (isMobile) {
      // Mobile: Cmd/Ctrl+Enter to send
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handleSend();
      }
      // Plain Enter: allow natural line break
    } else {
      // Desktop: Enter to send, Shift+Enter for line break
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
      // Shift+Enter: allow natural line break
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-background shrink-0 select-none">
      <AnimatePresence>
        {attachment && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="px-4 py-2 border-t bg-muted/30 flex items-center justify-between gap-2 overflow-hidden">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-10 h-10 bg-primary/10 rounded flex items-center justify-center shrink-0">
                <Paperclip className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{attachment.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{attachment.type.split('/')[0]}</p>
              </div>
            </div>
            {/* Botón de cerrar adjunto - simple div para evitar el accessory bar */}
            <div 
              role="button"
              className="h-8 w-8 hover:bg-muted rounded-full flex items-center justify-center shrink-0 cursor-pointer transition-colors"
              onClick={() => setAttachment(null)}
            >
              <X className="w-4 h-4" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="p-4 overflow-hidden pb-[calc(env(safe-area-inset-bottom,0px)+24px)] md:pb-4 border-t bg-background">
        <div className="flex items-end">
          <motion.div className="flex-1 overflow-hidden" layout transition={{ type: 'spring', damping: 30, stiffness: 350 }}>
            {isTouchDevice ? (
              <textarea
                ref={nativeTextareaRef}
                value={message}
                onChange={handleNativeTextareaChange}
                onKeyDown={handleKeyDown}
                spellCheck={false}
                autoCorrect="on"
                autoCapitalize="sentences"
                inputMode="text"
                rows={1}
                placeholder="Escribí acá..."
                className="min-h-[40px] max-h-[120px] w-full bg-background rounded-md border border-input px-3 py-2.5 text-[16px] outline-none focus:outline-none focus:ring-0 focus:border-input overflow-y-auto resize-none"
                style={{ boxShadow: 'none', WebkitTapHighlightColor: 'transparent' }}
              />
            ) : (
              <div
                ref={editableRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleEditableChange}
                onKeyDown={handleKeyDown}
                role="textbox"
                aria-multiline="true"
                spellCheck={false}
                autoCorrect="off"
                autoCapitalize="off"
                inputMode="text"
                className={cn(
                  "min-h-[40px] max-h-[120px] w-full bg-background rounded-md border border-input px-3 py-2.5 text-[16px] outline-none focus:outline-none focus:ring-0 focus:border-input overflow-y-auto whitespace-pre-wrap relative",
                  message === '' && "before:content-[attr(data-placeholder)] before:text-muted-foreground before:absolute before:left-3 before:top-2.5 before:pointer-events-none"
                )}
                style={{
                  resize: 'none',
                  boxShadow: 'none',
                  WebkitUserModify: 'read-write-plaintext-only',
                  WebkitTapHighlightColor: 'transparent',
                }}
                tabIndex={0}
                data-placeholder="Escribí acá..."
              />
            )}
          </motion.div>

          <AnimatePresence initial={false}>
            {!message.trim() && (
              <motion.div
                key="attachment-trigger"
                initial={{ width: 0, opacity: 0, marginLeft: 0 }}
                animate={{ width: 'auto', opacity: 1, marginLeft: 8 }}
                exit={{ width: 0, opacity: 0, marginLeft: 0 }}
                transition={{ type: 'spring', damping: 30, stiffness: 350 }}
                className="overflow-hidden flex items-end shrink-0"
              >
                {/* Ícono de adjunto - DIV simple para no activar el Done bar */}
                <div 
                  role="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={cn(
                    "h-10 w-10 bg-muted hover:bg-muted/80 rounded-md flex items-center justify-center cursor-pointer transition-colors",
                    uploading && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div 
            layout 
            transition={{ type: 'spring', damping: 30, stiffness: 350 }}
            className="flex items-end shrink-0 ml-2"
          >
            {/* Botón de enviar - DIV simple */}
            <div 
              role="button"
              onClick={handleSend}
              className={cn(
                "h-10 w-10 bg-primary text-primary-foreground rounded-md flex items-center justify-center cursor-pointer transition-colors hover:opacity-90",
                (!message.trim() && !attachment) && "opacity-50 cursor-not-allowed"
              )}
            >
              <Send className="w-4 h-4" />
            </div>
          </motion.div>
        </div>
      </div>

      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} accept="image/*,video/*,application/pdf,.doc,.docx" tabIndex={-1} autoComplete="off" />
    </motion.div>
  );
};
